import { useCallback, useState } from "react";
import { accountService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

/**
 * Custom hook for managing the MiContactCard view model.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {Object} params.contact - The contact object.
 * @param {string} params.contact.accountId - The ID of the associated account.
 * @param {string} params.contact.id - The unique identifier for the contact.
 * @param {string} params.contact.label - The label associated with the contact.
 * @param {boolean} params.contact.isPrimary - Indicates if the contact is primary.
 * @param {string} params.contact.relatedReferenceId - The reference ID related to the contact.
 * @param {Function} params.onRefresh - Function to refresh data after updating the contact.
 *
 * @returns {Object} Hook return values.
 * @returns {boolean} isLoading - Indicates if the operation is in progress.
 * @returns {Object} contactCategories - Mapping of contact categories by type, containing options and primary relationship types.
 * @returns {Function} getContactCategories - Function to fetch and set contact categories for a specific type.
 * @returns {Function} handleMakePrimary - Function to set the contact as primary.
 *
 * @example
 * const { isLoading, contactCategories, getContactCategories, handleMakePrimary } = useMiContactCardViewModel({
 *   contact: {
 *     accountId: "account-123",
 *     id: "contact-456",
 *     label: "Manager",
 *     isPrimary: false,
 *     relatedReferenceId: "ref-789",
 *   },
 *   onRefresh: () => console.log("Data refreshed"),
 * });
 */
export const useMiContactCardViewModel = ({ contact, onRefresh }) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [contactCategories, setContactCategories] = useState({});

  const getContactCategories = (type) => {
    stateManager.run(async () => {
      const response = await accountService.contactCategories.getByType({
        type,
      });
      if (response.status === HTTP_STATUSES.OK) {
        const activeCategories = response.data.filter(
          ({ isActive }) => isActive
        );
        const options = activeCategories.map(({ label }) => ({
          value: label,
          label,
        }));
        const primaryRelationshipTypes = new Set(
          activeCategories
            .filter(({ canBePrimary }) => canBePrimary)
            .map(({ label }) => label)
        );
        setContactCategories((prevContactCategories) => ({
          ...prevContactCategories,
          [type]: { options, primaryRelationshipTypes },
        }));
      }
    });
  };

  const deleteRelationshipOfContact = async (data) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.deleteContactRelationship({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: ` Contact relationship deleted successfully.`,
          },
          failure: {
            text: `Failed to delete contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
      }
    });

  const handleMakePrimary = useCallback(
    async () =>
      stateManager.run(async () => {
        const response =
          await accountService.contacts.updateContactRelationship({
            id: contact?.id,
            data: {
              accountId: contact?.accountId,
              label: contact?.label,
              isPrimary: true,
              RelatedReferenceId: contact?.relatedReferenceId,
            },
            toastMessageConfig: {
              success: {
                text: ` Contact updated successfully.`,
              },
              failure: {
                text: `Failed to update contact relationship.`,
              },
            },
          });
        if (response.status === HTTP_STATUSES.OK) {
          onRefresh();
        }
      }),
    [contact]
  );

  return {
    isLoading,
    contactCategories,
    getContactCategories,
    handleMakePrimary,
    deleteRelationshipOfContact,
  };
};
